import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';

export type BudgetPeriodType = 'weekly' | 'biweekly' | 'monthly' | 'custom';

export interface PersonalBudget {
  id: string;
  user_id: string;
  category_id: string;
  period_type: BudgetPeriodType;
  limit_amount: string;
  alert_threshold_warning: number;
  alert_threshold_critical: number;
  custom_start_date: string | null;
  custom_end_date: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface BudgetWithSpent extends PersonalBudget {
  spent_amount: number;
  category_name: string;
  category_icon: string | null;
  category_color: string | null;
}

export interface CreateBudgetInput {
  category_id: string;
  period_type: BudgetPeriodType;
  limit_amount: number;
  alert_threshold_warning?: number;
  alert_threshold_critical?: number;
  custom_start_date?: string;
  custom_end_date?: string;
}

export interface UpdateBudgetInput {
  period_type?: BudgetPeriodType;
  limit_amount?: number;
  alert_threshold_warning?: number;
  alert_threshold_critical?: number;
  custom_start_date?: string | null;
  custom_end_date?: string | null;
  is_active?: boolean;
}

interface UsePersonalBudgetsOptions {
  includeInactive?: boolean;
}

export function usePersonalBudgets(options?: UsePersonalBudgetsOptions) {
  const { user, session, initialized } = useAuth();
  const queryClient = useQueryClient();

  const queryParams = new URLSearchParams();
  if (options?.includeInactive) queryParams.set('includeInactive', 'true');
  // Send the user's local date so the server uses the correct period (server runs in UTC)
  const now = new Date();
  const localDate = [
    now.getFullYear(),
    String(now.getMonth() + 1).padStart(2, '0'),
    String(now.getDate()).padStart(2, '0'),
  ].join('-');
  queryParams.set('localDate', localDate);
  const queryString = queryParams.toString();
  const endpoint = `/personal/budgets?${queryString}`;

  const {
    data: budgets,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['personalBudgets', user?.id, options?.includeInactive],
    queryFn: async (): Promise<BudgetWithSpent[]> => {
      if (!session?.access_token) {
        return [];
      }

      const response = await api.get<{ budgets: BudgetWithSpent[] }>(
        endpoint,
        session.access_token
      );

      if (!response.success) {
        throw new Error(response.error || 'Failed to fetch budgets');
      }

      return response.data?.budgets ?? [];
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
    retry: 1,
    enabled: initialized && !!user && !!session?.access_token,
  });

  const invalidateBudgets = () =>
    queryClient.invalidateQueries({ queryKey: ['personalBudgets'] });

  const createBudget = useMutation({
    mutationFn: async (input: CreateBudgetInput) => {
      if (!session?.access_token) throw new Error('Not authenticated');
      const response = await api.post<{ budget: PersonalBudget }>(
        '/personal/budgets',
        input,
        session.access_token
      );
      if (!response.success) throw new Error(response.error || 'Failed to create budget');
      return response.data!.budget;
    },
    onSuccess: invalidateBudgets,
  });

  const updateBudget = useMutation({
    mutationFn: async ({ id, ...input }: UpdateBudgetInput & { id: string }) => {
      if (!session?.access_token) throw new Error('Not authenticated');
      const response = await api.patch<{ budget: PersonalBudget }>(
        `/personal/budgets/${id}`,
        input,
        session.access_token
      );
      if (!response.success) throw new Error(response.error || 'Failed to update budget');
      return response.data!.budget;
    },
    onSuccess: invalidateBudgets,
  });

  const deleteBudget = useMutation({
    mutationFn: async (id: string) => {
      if (!session?.access_token) throw new Error('Not authenticated');
      const response = await api.delete<{ message: string }>(
        `/personal/budgets/${id}`,
        session.access_token
      );
      if (!response.success) throw new Error(response.error || 'Failed to delete budget');
      return response.data!;
    },
    onSuccess: invalidateBudgets,
  });

  return {
    budgets: budgets ?? [],
    isLoading,
    error,
    refetch,
    createBudget,
    updateBudget,
    deleteBudget,
  };
}
